import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Tooltip, Polygon, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useTranslation } from 'react-i18next';
import { gestureHandlingText } from '../../utils/leafletGestureHandling';
import { FreshnessThresholds } from '../../utils/scoutingUtils';
import { formatDate } from '../../dateFormat';

// Fix default marker icons in Leaflet with Webpack/CRA
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

// @ts-ignore
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
});

export interface InspectionPoint {
  treeIndex: number;
  pointIndex: number;
  pestId: string;
  timestamp: string;
  latitude: number;
  longitude: number;
  accuracy?: number;
  hardwareTimestamp?: number;
  /** Populated when points come from a multi-orchard dashboard context. */
  scouterName?: string;
}

export interface OrchardData {
  _id: string;
  orchardName: string;
  farmName: string;
  boundary?: {
    type: 'Polygon';
    coordinates: number[][][];
  };
  location?: {
    type: 'Point';
    coordinates: number[];
  };
}

export interface MapOptions {
  pestMap?: Record<string, string>;
  scouterName?: string;
  recordId?: string;
}

/** Orchard with a pre-computed freshness hex color for heatmap rendering. */
export interface HeatmapOrchard extends OrchardData {
  freshnessColor: string;
  /** Upper-cased block id of the latest observation (for the V2 review deep-link). */
  blockId?: string | null;
  /** ISO date string of the most recent scouting session. */
  lastScoutedDate?: string | null;
  /** Name of the scouter on the latest session. */
  lastScouterName?: string | null;
  /** MongoDB _id of the latest scouting record (for "View Record" link). */
  latestRecordId?: string | null;
  /** Brief summary of the most recent pest/disease findings. */
  findingsSummary?: string | null;
}

interface ScoutingMapProps {
  points: InspectionPoint[];
  orchard: OrchardData | null;
  options?: MapOptions;
  /** When provided, renders multi-orchard heatmap polygons colored by freshness. */
  heatmapOrchards?: HeatmapOrchard[];
  /** Controls how marker colors are assigned. Defaults to 'pest'. */
  colorBy?: 'pest' | 'scouter';
  /** Freshness thresholds used to derive heatmap polygon colors. */
  thresholds?: FreshnessThresholds;
  /** Whether the current user can view scouting records (shows "View Record" link in popup). */
  canViewRecords?: boolean;
}

/** Creates a colored circle icon for a marker */
function makeCircleIcon(color: string): L.DivIcon {
  return L.divIcon({
    className: '',
    html: `<div style="
      width:14px;height:14px;border-radius:50%;
      background:${color};border:2px solid white;
      box-shadow:0 1px 3px rgba(0,0,0,0.5);
    "></div>`,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
    popupAnchor: [0, -10],
  });
}

const PEST_COLORS = [
  '#e74c3c', '#3498db', '#f39c12', '#9b59b6',
  '#1abc9c', '#e67e22', '#2ecc71', '#c0392b',
];

/** Extracts [lat, lng] pairs from a GeoJSON Polygon boundary. */
function extractBoundaryLatLngs(
  boundary: OrchardData['boundary']
): L.LatLngExpression[] {
  const result: L.LatLngExpression[] = [];
  boundary?.coordinates?.[0]?.forEach(([lng, lat]) => result.push([lat, lng]));
  return result;
}

/** Auto-fit map bounds to orchard polygon(s) or inspection points */
const MapBoundsController: React.FC<{
  points: InspectionPoint[];
  orchard: OrchardData | null;
  heatmapOrchards?: HeatmapOrchard[];
}> = ({ points, orchard, heatmapOrchards }) => {
  const map = useMap();

  // In heatmap mode only boundaries drive the zoom — GPS points are excluded
  // because scouted inspection data can contain erroneous coordinates that
  // would zoom the map out to an incorrect location.
  const isHeatmapMode = heatmapOrchards && heatmapOrchards.length > 0;

  useEffect(() => {
    const latLngs: L.LatLngExpression[] = [];

    // Single orchard boundary (single-record view)
    latLngs.push(...extractBoundaryLatLngs(orchard?.boundary));

    // Multiple heatmap orchard boundaries
    if (heatmapOrchards) {
      heatmapOrchards.forEach(o => latLngs.push(...extractBoundaryLatLngs(o.boundary)));
    }

    // Only include GPS inspection points when NOT in heatmap mode.
    // In the dashboard heatmap, the KML boundary polygons define the farm
    // extent; individual GPS points may contain bad data and must not
    // influence the auto-zoom.
    if (!isHeatmapMode) {
      points.forEach(p => latLngs.push([p.latitude, p.longitude]));
    }

    // Delay so the map container has been painted to its correct size before
    // we call invalidateSize (which recalculates the pixel dimensions) and then
    // fitBounds (which needs accurate pixel dimensions to compute the right zoom).
    const timer = setTimeout(() => {
      try {
        map.invalidateSize();
        if (latLngs.length > 0) {
          const bounds = L.latLngBounds(latLngs);
          if (bounds && bounds.isValid()) {
            map.fitBounds(bounds, { padding: [30, 30] });
          }
        }
      } catch {
        // fitBounds unavailable in test environment; ignore
      }
    }, 150);
    return () => clearTimeout(timer);
  }, [map, points, orchard, heatmapOrchards, isHeatmapMode]);

  return null;
};

const ScoutingMap: React.FC<ScoutingMapProps> = ({ points, orchard, options = {}, heatmapOrchards, colorBy = 'pest', canViewRecords = false }) => {
  const { t } = useTranslation();
  const { pestMap = {}, scouterName } = options;

  // Derive a color key for each point based on the colorBy mode.
  const getPointKey = (p: InspectionPoint): string =>
    colorBy === 'scouter' ? (p.scouterName ?? 'Unknown') : p.pestId;

  // Build unique key list and assign colors.
  const uniqueKeys = Array.from(new Set(points.map(getPointKey)));
  const colorMap: Record<string, string> = {};
  uniqueKeys.forEach((key, idx) => {
    colorMap[key] = (colorBy === 'pest' && key === '')
      ? '#27ae60'   // green = no pest
      : PEST_COLORS[idx % PEST_COLORS.length];
  });

  // Legend toggle state: which keys are currently hidden
  const [hiddenKeys, setHiddenKeys] = useState<Set<string>>(new Set());

  // Heatmap polygon hover state — used to highlight the selected orchard shape
  const [hoveredOrchardId, setHoveredOrchardId] = useState<string | null>(null);

  const toggleKey = (key: string) => {
    setHiddenKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  // Guard against points without valid coordinates: rendering a Leaflet <Marker>
  // with an undefined/NaN lat or lng throws ("Invalid LatLng object") and crashes
  // the whole map (and the scouting module via its ErrorBoundary). Some records —
  // e.g. aggregate-only records bridged from the Field Scout flow, or legacy
  // erroneous data — carry points without coordinates; skip those markers.
  const visiblePoints = points.filter(
    p =>
      !hiddenKeys.has(getPointKey(p)) &&
      Number.isFinite(p.latitude) &&
      Number.isFinite(p.longitude),
  );

  // Default map center: orchard centroid, first point, or global default
  const defaultCenter: L.LatLngExpression = orchard?.location
    ? [orchard.location.coordinates[1], orchard.location.coordinates[0]]
    : points.length > 0
    ? [points[0].latitude, points[0].longitude]
    : [-33.93, 18.84];

  const boundaryPositions: [number, number][] | undefined =
    orchard?.boundary?.coordinates?.[0]?.map(([lng, lat]) => [lat, lng]);

  const getKeyLabel = (key: string) =>
    colorBy === 'scouter' ? key : (key === '' ? 'No Pest' : (pestMap[key] || key));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', height: '100%' }}>
      {/* Filter legend (pest or scouter) */}
      {uniqueKeys.length > 0 && (
        <section
          aria-label="Legend"
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '6px',
            padding: '8px',
            background: '#f8f9fa',
            borderRadius: '6px',
            border: '1px solid #dee2e6',
          }}
        >
          <span id="scouting-map-legend-title" style={{ fontWeight: 600, fontSize: '0.8rem', alignSelf: 'center' }}>
            {colorBy === 'scouter' ? 'Filter by Scouter:' : 'Filter by Pest:'}
          </span>
          {uniqueKeys.map(key => {
            const label = getKeyLabel(key);
            const color = colorMap[key];
            const hidden = hiddenKeys.has(key);
            return (
              <button
                key={key || '__no_pest__'}
                onClick={() => toggleKey(key)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '2px 8px',
                  borderRadius: '12px',
                  border: `2px solid ${color}`,
                  background: hidden ? 'white' : color,
                  color: hidden ? color : 'white',
                  cursor: 'pointer',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  opacity: hidden ? 0.6 : 1,
                }}
                aria-pressed={!hidden}
                title={hidden ? `Show ${label} markers` : `Hide ${label} markers`}
              >
                {label}
              </button>
            );
          })}
        </section>
      )}

      {/* Map */}
      {/* Suppress Leaflet's default rectangular SVG focus outline on polygon paths */}
      <style>{`.leaflet-interactive:focus { outline: none; }`}</style>
      <div
        role="region"
        aria-label="Scouting map"
        style={{ flex: 1, width: '100%', borderRadius: '8px', overflow: 'hidden', minHeight: 0 }}
      >
        <MapContainer
          center={defaultCenter}
          zoom={15}
          gestureHandling={true}
          gestureHandlingOptions={gestureHandlingText(t)}
          style={{ height: '100%', width: '100%' }}
        >
          <MapBoundsController points={points} orchard={orchard} heatmapOrchards={heatmapOrchards} />
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          {/* Single orchard boundary polygon (single-record view) */}
          {boundaryPositions && !heatmapOrchards && (
            <Polygon
              positions={boundaryPositions}
              pathOptions={{ color: '#2c7a2c', fillColor: '#2c7a2c', fillOpacity: 0.12, weight: 2 }}
            >
              <Tooltip sticky>
                {orchard?.orchardName}
              </Tooltip>
            </Polygon>
          )}

          {/* Heatmap polygons — one per orchard, shaded by freshness color */}
          {heatmapOrchards?.map(o => {
            const positions = o.boundary?.coordinates?.[0]?.map(
              ([lng, lat]) => [lat, lng] as [number, number]
            );
            if (!positions) return null;

            const scoutedDateDisplay = o.lastScoutedDate
              ? formatDate(o.lastScoutedDate)
              : 'Never scouted';

            const isHovered = hoveredOrchardId === o._id;

            return (
              <Polygon
                key={o._id}
                positions={positions}
                pathOptions={{
                  color: isHovered ? '#fff' : o.freshnessColor,
                  fillColor: o.freshnessColor,
                  fillOpacity: isHovered ? 0.75 : 0.45,
                  weight: isHovered ? 4 : 2,
                }}
                eventHandlers={{
                  mouseover: () => setHoveredOrchardId(o._id),
                  mouseout: () => setHoveredOrchardId(null),
                  click: () => setHoveredOrchardId(o._id),
                }}
              >
                <Tooltip sticky>{o.orchardName}</Tooltip>
                <Popup>
                  <div style={{ minWidth: '160px' }}>
                    <strong style={{ fontSize: '0.9rem' }}>{o.orchardName}</strong>
                    <div style={{ marginTop: '6px', fontSize: '0.8rem' }}>
                      <div><span style={{ color: '#6c757d' }}>Last scouted:</span> {scoutedDateDisplay}</div>
                      {o.lastScouterName && (
                        <div><span style={{ color: '#6c757d' }}>Scouter:</span> {o.lastScouterName}</div>
                      )}
                      {o.findingsSummary && (
                        <div style={{ marginTop: '4px' }}>
                          <span style={{ color: '#6c757d' }}>Findings:</span> {o.findingsSummary}
                        </div>
                      )}
                    </div>
                    {canViewRecords && (
                      <div style={{ marginTop: '8px' }}>
                        <a
                          href={`/scouting/review?block=${encodeURIComponent(o.blockId ?? o.orchardName.toUpperCase().trim())}`}
                          className="btn btn-sm btn-outline-primary"
                          style={{ fontSize: '0.75rem' }}
                        >
                          View Records
                        </a>
                      </div>
                    )}
                  </div>
                </Popup>
              </Polygon>
            );
          })}

          {/* Inspection point markers */}
          {visiblePoints.map((point, idx) => {
            const key = getPointKey(point);
            const color = colorMap[key] ?? '#7f8c8d';
            const pestLabel = colorBy === 'scouter'
              ? (pestMap[point.pestId] || point.pestId || 'No Pest')
              : getKeyLabel(point.pestId);
            const icon = makeCircleIcon(color);
            const displayScouter = point.scouterName ?? scouterName;

            return (
              <Marker
                key={`${point.treeIndex}-${point.pointIndex}-${idx}`}
                position={[point.latitude, point.longitude]}
                icon={icon}
              >
                <Tooltip>
                  <div>
                    <strong>Tree {point.treeIndex}</strong>
                    {displayScouter && <div>Scouter: {displayScouter}</div>}
                    <div>{pestLabel}</div>
                  </div>
                </Tooltip>
                <Popup>
                  <div>
                    <strong>Tree #{point.treeIndex}</strong>
                    {displayScouter && <div>Scouter: {displayScouter}</div>}
                    <div>Pest: {pestLabel}</div>
                    {point.accuracy !== undefined && (
                      <div>Accuracy: {point.accuracy.toFixed(1)}m</div>
                    )}
                    <div style={{ marginTop: '6px' }}>
                      {canViewRecords && (
                        <a
                          href={orchard ? `/scouting/review?block=${encodeURIComponent(orchard.orchardName.toUpperCase().trim())}` : '/scouting/review'}
                          className="btn btn-sm btn-outline-primary"
                          style={{ fontSize: '0.75rem' }}
                        >
                          View Records
                        </a>
                      )}
                    </div>
                  </div>
                </Popup>
              </Marker>
            );
          })}
        </MapContainer>
      </div>
    </div>
  );
};

export { ScoutingMap };
